// @section 1 Current-user context (Atlas)
// Resolves the signed-in operator once (GET /api/hq/me) and shares it app-wide
// so per-user surfaces (My Day, CRM, …) render the caller's own data instead of
// the hardcoded ADAM_SUBJECT. Mounted inside AuthBoot, so it only fetches once
// an MSAL session exists.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, type Me } from '../api';
import { toast } from '../components/Toast';

// @block 1.1 Context shape — me is null until /me resolves; error surfaces failures
type MeState = { me: Me | null; error: string | null };
const MeContext = createContext<MeState>({ me: null, error: null });

// @block 1.2 Provider — fetch once on mount
export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getMe()
      .then((m) => { if (alive) setMe(m); })
      .catch((e) => {
        if (!alive) return;
        const msg = String(e);
        setError(msg);
        toast(`Could not load your profile: ${msg}`);
      });
    return () => { alive = false; };
  }, []);

  return <MeContext.Provider value={{ me, error }}>{children}</MeContext.Provider>;
}

// @block 1.3 Hooks — useMe() for the full state, useMeUser() for just the user
export function useMe(): MeState {
  return useContext(MeContext);
}
export function useMeUser(): Me | null {
  return useContext(MeContext).me;
}
